import type {
  CustodianActivityStates,
  FetchCustodianActivitiesFilterForGrid,
  FetchCustodianActivitiesInputForGrid,
} from '@generated/graphql';
import {
  ACTIVITIES_ANY_FILTER,
  isActivitiesAgeFilterDays,
  processDateBeforeCutoff,
} from './activities.config';

export type BuildActivitiesGridFilterInput = {
  orgId: string | undefined;
  state: CustodianActivityStates;
  transactionType: string;
  agingFilter: string;
  search: string;
};

export const buildActivitiesGridFilter = (
  input: BuildActivitiesGridFilterInput,
): FetchCustodianActivitiesFilterForGrid | null => {
  const { orgId, state, transactionType, agingFilter, search } = input;
  if (!orgId) {
    return null;
  }

  // `state: RECONCILED` is labeled "Reconciled (today)" in the UI. The today
  // window is backend-computed in UTC for this grid filter (same contract as
  // `reconciledTodayCount`) — FetchCustodianActivitiesFilterForGrid has no
  // FE date/reconciledAt input for that scope.
  const filter: FetchCustodianActivitiesFilterForGrid = {
    organizationId: orgId,
    state,
  };

  if (transactionType !== ACTIVITIES_ANY_FILTER) {
    filter.transactionType = transactionType;
  }

  const minAgeDays =
    agingFilter === ACTIVITIES_ANY_FILTER
      ? null
      : Number.parseInt(agingFilter, 10) || null;
  if (isActivitiesAgeFilterDays(minAgeDays)) {
    filter.processDateBefore = processDateBeforeCutoff(minAgeDays);
  }

  const trimmedSearch = search.trim();
  if (trimmedSearch) {
    filter.search = trimmedSearch;
  }

  return filter;
};

export type BuildFetchActivitiesGridVariablesInput = {
  gridFilter: FetchCustodianActivitiesFilterForGrid | null;
  pageSize: number;
  page: number;
};

export const shouldSuppressActivitiesGridResults = (
  gridLoading: boolean,
  gridFilterKey: string,
  resolvedGridFilterKey: string | null,
): boolean => gridLoading && gridFilterKey !== resolvedGridFilterKey;

export const buildFetchActivitiesGridVariables = (
  input: BuildFetchActivitiesGridVariablesInput,
): { input: FetchCustodianActivitiesInputForGrid } => {
  const { gridFilter, pageSize, page } = input;
  return {
    input: {
      filter: gridFilter ?? undefined,
      pagination: {
        perPage: pageSize,
        offSet: (page - 1) * pageSize,
      },
    },
  };
};
